import React, { useState, useRef } from "react";
import {
  Button,
  Card,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Snackbar,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Box,
  Chip,
  CardContent,
  CardActions,
  Divider,
} from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import DownloadIcon from "@mui/icons-material/Download";
import ErrorIcon from "@mui/icons-material/Error";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import InfoIcon from "@mui/icons-material/Info";
import { saveAs } from "file-saver";

const EXCEL_TEMPLATE_COLUMNS = [
  "CenterQr",
  "Client Id",
  "Service provided for",
  "Specific requirement",
  "service_provided_client_id",
  "Country code",
  "Mobile number",
  "Customer name",
  "House number",
  "Locality",
  "City",
  "State",
  "Pincode",
  "Alternate number",
  "Country",
  "Time preference type",
  "Specific date and time",
  "Ticket price",
  "Offer code",
  "Is paid ticket",
  "ticketCreatedBy",
  "service_provided_due_amount",
];

const BulkUploadComponent = ({
  clientsList = [],
  currentClientId,
  BASE_URL,
  onShowDialog,
}) => {
  const fileInputRef = useRef(null);

  const [state, setState] = useState({
    bulkUploadMode: false,
    selectedFile: null,
    fileName: "",
    uploadedTickets: [],
    rejectedTickets: [],
    bulkUploadClientId: "",
    bulkUploadErrors: {},
    bulkUploadStats: {
      total: 0,
      accepted: 0,
      rejected: 0,
    },
    showErrorDetails: false,
    showSuccessDetails: false,
    isRegistering: false,
    toast: {
      open: false,
      msg: "",
      duration: 5000,
      type: "",
    },
  });

  const closeDialog = () => {
    setState((prev) => ({
      ...prev,
      toast: { ...prev.toast, open: false },
    }));
  };

  const showDialog = (open, type, msg) => {
    if (onShowDialog) {
      onShowDialog(open, type, msg);
    } else {
      setState((prev) => ({
        ...prev,
        toast: { open, type, msg, duration: 5000 },
      }));
    }
  };

  const handleBulkUploadToggle = () => {
    setState((prev) => ({
      ...prev,
      bulkUploadMode: !prev.bulkUploadMode,
      selectedFile: null,
      fileName: "",
      uploadedTickets: [],
      rejectedTickets: [],
      showErrorDetails: false,
      showSuccessDetails: false,
    }));
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      const validTypes = [
        "text/csv",
        "application/vnd.ms-excel",
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      ];
      if (!validTypes.includes(file.type)) {
        showDialog(true, "error", "Please upload a valid CSV or Excel file");
        return;
      }

      setState((prev) => ({
        ...prev,
        selectedFile: file,
        fileName: file.name,
        uploadedTickets: [],
        rejectedTickets: [],
        showErrorDetails: false,
        showSuccessDetails: false,
      }));
    }
  };

  const downloadTemplate = () => {
    const header = EXCEL_TEMPLATE_COLUMNS.join(",");
    const exampleData = [
      "",
      currentClientId || "CL-86",
      "AIR CONDITIONER - AND GEYSER",
      "Fix leakage",
      "1234567890",
      "+91",
      "9876543210",
      "Dev",
      "123A",
      "Green Street",
      "New Delhi",
      "Delhi",
      "110001",
      "6284714797",
      "INDIA",
      "IMMEDIATELY",
      "",
      "1000",
      "ONIT2026",
      "Yes",
      "IMPORT_VIA_EXCEL",
      "500",
    ].join(",");
    const csvContent = [header, exampleData].join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    saveAs(blob, "ticket_upload_template.csv");
  };

  const handleBulkSubmit = async () => {
    const { selectedFile, bulkUploadClientId } = state;

    if (!selectedFile) {
      showDialog(true, "error", "Please select a file to upload");
      return;
    }

    // Use current client ID if available, otherwise use selected client
    const clientIdToUse = currentClientId || bulkUploadClientId;

    if (!clientIdToUse) {
      showDialog(true, "error", "Please select a client for bulk upload");
      return;
    }

    setState((prev) => ({ ...prev, isRegistering: true }));

    const formData = new FormData();
    formData.append("ticketsCsvFile", selectedFile);

    try {
      const response = await fetch(
        `${BASE_URL}ticket/createBulkTickets?clientId=${clientIdToUse}`,
        {
          method: "POST",
          body: formData,
        }
      );

      const result = await response.json();

      if (result.success === 1) {
        setState((prev) => ({
          ...prev,
          uploadedTickets: result.data?.acceptedTicketDetails || [],
          rejectedTickets: result.data?.rejectedTicketDetails || [],
          bulkUploadErrors: result.data?.errorRowWise || {},
          bulkUploadStats: {
            total: result.data?.recievedTickets || 0,
            accepted: result.data?.acceptedTickets || 0,
            rejected: result.data?.rejectedTickets || 0,
          },
          selectedFile: null,
          fileName: "",
          isRegistering: false,
          showErrorDetails: result.data?.rejectedTickets > 0,
          showSuccessDetails: result.data?.acceptedTickets > 0,
        }));

        const message = result.data.doesErrorExists
          ? `Processed ${result.data.recievedTickets} tickets: ${result.data.acceptedTickets} accepted, ${result.data.rejectedTickets} rejected`
          : `Successfully uploaded ${result.data.acceptedTickets} tickets!`;

        showDialog(
          true,
          result.data.doesErrorExists ? "warning" : "success",
          message
        );
      } else {
        showDialog(true, "error", result.message || "Upload failed");
        setState((prev) => ({ ...prev, isRegistering: false }));
      }
    } catch (error) {
      console.error("Bulk Upload Error:", error);
      showDialog(true, "error", "Something went wrong during upload.");
      setState((prev) => ({ ...prev, isRegistering: false }));
    }
  };

  const toggleErrorDetails = () => {
    setState((prev) => ({
      ...prev,
      showErrorDetails: !prev.showErrorDetails,
    }));
  };

  const toggleSuccessDetails = () => {
    setState((prev) => ({
      ...prev,
      showSuccessDetails: !prev.showSuccessDetails,
    }));
  };

  const handleBulkUploadClientChange = (e) => {
    const selectedClient = clientsList.find(
      (client) => client._id === e.target.value
    );
    setState((prev) => ({
      ...prev,
      bulkUploadClientId: selectedClient?.client_id || "",
    }));
  };

  // If current client is available, don't show client selection
  const shouldShowClientSelection = !currentClientId && clientsList.length > 0;

  return (
    <>
      {/* Local Toast Notification */}
      {state.toast.open && !onShowDialog && (
        <Snackbar
          anchorOrigin={{ vertical: "top", horizontal: "right" }}
          open={state.toast.open}
          autoHideDuration={state.toast.duration}
          onClose={closeDialog}
        >
          <Alert
            onClose={closeDialog}
            severity={state.toast.type}
            className="w-full"
            elevation={6}
          >
            {state.toast.msg}
          </Alert>
        </Snackbar>
      )}

      <Card className="rounded-xl shadow-lg border border-gray-100 overflow-hidden">
        <CardContent className="p-6">
          <div className="flex items-center justify-between mb-6">
            <Typography variant="h5" className="font-bold text-gray-800">
              Bulk Request Upload
            </Typography>
            <Chip
              label={state.bulkUploadMode ? "Active" : "Inactive"}
              color={state.bulkUploadMode ? "primary" : "default"}
              variant="outlined"
              size="small"
            />
          </div>

          <Typography variant="body2" color="textSecondary" className="mb-6">
            Upload multiple requests at once using Excel or CSV files. Download
            the template to ensure proper formatting.
          </Typography>

          <CardActions className="p-0 gap-3 flex-wrap">
            <Button
              variant={state.bulkUploadMode ? "contained" : "outlined"}
              color="primary"
              onClick={handleBulkUploadToggle}
              startIcon={<CloudUploadIcon />}
              className={`rounded-lg px-6 py-2 font-medium ${
                state.bulkUploadMode
                  ? "bg-teal-600 hover:bg-teal-700 shadow-md"
                  : "border-teal-600 text-teal-600 hover:bg-teal-50"
              }`}
            >
              {state.bulkUploadMode ? "Cancel Upload" : "Bulk Upload"}
            </Button>

            <Button
              variant="outlined"
              color="primary"
              onClick={downloadTemplate}
              startIcon={<DownloadIcon />}
              className="rounded-lg px-6 py-2 font-medium border-teal-600 text-teal-600 hover:bg-teal-50"
            >
              Download Template
            </Button>
          </CardActions>

          {state.bulkUploadMode && (
            <Box className="mt-6 p-6 bg-gray-50 rounded-xl border border-gray-200">
              {/* Client Selection - Only show if no current client */}
              {shouldShowClientSelection && (
                <FormControl fullWidth className="mb-6">
                  <InputLabel
                    id="bulk-upload-client-label"
                    className="text-gray-600"
                  >
                    Select Client for Bulk Upload
                  </InputLabel>
                  <Select
                    labelId="bulk-upload-client-label"
                    value={
                      clientsList.find(
                        (client) =>
                          client.client_id === state.bulkUploadClientId
                      )?._id || ""
                    }
                    onChange={handleBulkUploadClientChange}
                    label="Select Client for Bulk Upload"
                    required
                    className="bg-white rounded-lg"
                  >
                    {clientsList?.map((client) => (
                      <MenuItem key={client._id} value={client._id}>
                        <div className="flex flex-col">
                          <span className="font-medium">
                            {client.client_name}
                          </span>
                          <span className="text-sm text-gray-500">
                            CLIENT ID: {client.client_id}
                          </span>
                        </div>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}

              {/* File Upload Section */}
              <div className="space-y-4">
                <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
                  <input
                    accept=".xlsx,.xls,.csv"
                    style={{ display: "none" }}
                    id="bulk-upload-file"
                    type="file"
                    onChange={handleFileChange}
                    ref={fileInputRef}
                  />
                  <label htmlFor="bulk-upload-file" className="flex-1">
                    <Button
                      variant="outlined"
                      component="span"
                      color="primary"
                      startIcon={<CloudUploadIcon />}
                      className="w-full sm:w-auto rounded-lg px-6 py-3 border-2 border-dashed border-teal-300 hover:border-teal-400 hover:bg-teal-50 text-teal-700 font-medium"
                    >
                      Choose File
                    </Button>
                  </label>

                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleBulkSubmit}
                    disabled={
                      !state.selectedFile ||
                      (!currentClientId && !state.bulkUploadClientId) ||
                      state.isRegistering
                    }
                    startIcon={
                      state.isRegistering ? (
                        <CircularProgress size={20} />
                      ) : null
                    }
                    className="rounded-lg px-8 py-3 bg-teal-600 hover:bg-teal-700 shadow-md font-medium disabled:bg-gray-400"
                  >
                    {state.isRegistering ? "Processing..." : "Upload & Process"}
                  </Button>
                </div>

                {state.fileName && (
                  <div className="flex items-center gap-2 p-3 bg-white rounded-lg border border-teal-200">
                    <CheckCircleIcon
                      className="text-teal-600"
                      fontSize="small"
                    />
                    <Typography
                      variant="body2"
                      className="font-medium text-gray-700"
                    >
                      Selected file:{" "}
                      <span className="text-teal-600">{state.fileName}</span>
                    </Typography>
                  </div>
                )}

                <div className="flex items-center gap-2 text-sm text-gray-600 bg-blue-50 p-3 rounded-lg">
                  <InfoIcon fontSize="small" className="text-blue-500" />
                  <span>
                    Supported formats: .xlsx, .xls, .csv • Download template for
                    reference
                  </span>
                </div>
              </div>
            </Box>
          )}

          {/* Upload Results */}
          {state.rejectedTickets.length > 0 && (
            <Box className="mt-6 p-1">
              <Card className="border border-red-200 rounded-xl shadow-sm">
                <CardContent className="p-4">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <div className="flex items-center gap-3">
                      <ErrorIcon className="text-red-500" />
                      <div>
                        <Typography
                          variant="h6"
                          className="text-red-700 font-semibold"
                        >
                          Upload Summary
                        </Typography>
                        <Typography variant="body2" className="text-red-600">
                          {state.bulkUploadStats.accepted} Accepted •{" "}
                          {state.bulkUploadStats.rejected} Rejected
                        </Typography>
                      </div>
                    </div>
                    <Button
                      onClick={toggleErrorDetails}
                      size="small"
                      color="error"
                      variant="outlined"
                      className="rounded-lg"
                    >
                      {state.showErrorDetails ? "Hide Details" : "Show Details"}
                    </Button>
                  </div>

                  {state.showErrorDetails && (
                    <div className="mt-4">
                      <Divider className="mb-4" />
                      <Typography
                        variant="subtitle2"
                        className="text-red-600 mb-3 font-medium"
                      >
                        Error Details:
                      </Typography>
                      <div className="max-h-80 overflow-auto rounded-lg">
                        <TableContainer component={Paper} elevation={1}>
                          <Table size="small" aria-label="error details">
                            <TableHead>
                              <TableRow className="bg-red-50">
                                <TableCell className="font-semibold">
                                  Row
                                </TableCell>
                                <TableCell className="font-semibold">
                                  Customer
                                </TableCell>
                                <TableCell className="font-semibold">
                                  Client ID
                                </TableCell>
                                <TableCell className="font-semibold">
                                  Errors
                                </TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {state.rejectedTickets.map((ticket, index) => (
                                <TableRow
                                  key={index}
                                  className={
                                    index % 2 === 0 ? "bg-white" : "bg-red-25"
                                  }
                                >
                                  <TableCell className="font-medium">
                                    {ticket.row}
                                  </TableCell>
                                  <TableCell>
                                    {ticket.customer_name || "N/A"}
                                  </TableCell>
                                  <TableCell>
                                    {ticket.service_provided_client_id || "N/A"}
                                  </TableCell>
                                  <TableCell className="text-red-500 max-w-xs truncate">
                                    {Array.isArray(ticket.errors)
                                      ? ticket.errors.join(", ")
                                      : JSON.stringify(ticket.errors)}
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            </Box>
          )}

          {state.uploadedTickets.length > 0 && (
            <Box className="mt-6 p-1">
              <Card className="border border-green-200 rounded-xl shadow-sm">
                <CardContent className="p-4">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <div className="flex items-center gap-3">
                      <CheckCircleIcon className="text-green-500" />
                      <div>
                        <Typography
                          variant="h6"
                          className="text-green-700 font-semibold"
                        >
                          Successfully Created Requests
                        </Typography>
                        <Typography variant="body2" className="text-green-600">
                          {state.uploadedTickets.length} requests processed
                          successfully
                        </Typography>
                      </div>
                    </div>
                    <Button
                      onClick={toggleSuccessDetails}
                      size="small"
                      color="success"
                      variant="outlined"
                      className="rounded-lg border-green-500 text-green-600 hover:bg-green-50"
                    >
                      {state.showSuccessDetails
                        ? "Hide Details"
                        : "Show Details"}
                    </Button>
                  </div>

                  {state.showSuccessDetails && (
                    <div className="mt-4">
                      <Divider className="mb-4" />
                      <div className="max-h-80 overflow-auto rounded-lg">
                        <TableContainer component={Paper} elevation={1}>
                          <Table size="small" aria-label="success details">
                            <TableHead>
                              <TableRow className="bg-green-50">
                                <TableCell className="font-semibold">
                                  Ticket ID
                                </TableCell>
                                <TableCell className="font-semibold">
                                  Customer
                                </TableCell>
                                <TableCell className="font-semibold">
                                  Client ID
                                </TableCell>
                                <TableCell className="font-semibold">
                                  Ticket Price
                                </TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {state.uploadedTickets.map((ticket, index) => (
                                <TableRow
                                  key={index}
                                  className={
                                    index % 2 === 0 ? "bg-white" : "bg-green-25"
                                  }
                                >
                                  <TableCell className="font-medium text-teal-600">
                                    {ticket.ticket_id}
                                  </TableCell>
                                  <TableCell>{ticket.customer_name}</TableCell>
                                  <TableCell>
                                    {ticket.service_provided_client_id || "N/A"}
                                  </TableCell>
                                  <TableCell className="font-medium">
                                    ₹{ticket.ticket_price}
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            </Box>
          )}
        </CardContent>
      </Card>
    </>
  );
};

export default BulkUploadComponent;
